import { TextField, TextFieldProps, Box, Chip, Tooltip, keyframes } from '@mui/material';
import { Warning, Check, Edit } from '@mui/icons-material';
import { useFormData } from '../../contexts/FormDataContext';
import { useConfidenceToggle } from '../../contexts/ConfidenceToggleContext';
import { useRef, useEffect, useState } from 'react';

// Glow animation for AI-updated fields
const glowPulse = keyframes`
  0% { box-shadow: 0 0 4px 1px rgba(0, 150, 136, 0.4); }
  50% { box-shadow: 0 0 10px 3px rgba(0, 150, 136, 0.6); }
  100% { box-shadow: 0 0 4px 1px rgba(0, 150, 136, 0.4); }
`;

/**
 * ✅ NEW: Default formatter for complex values (arrays, objects)
 * Handles medication arrays, ROS objects, etc.
 */
function formatDefaultValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  // If already a string, return as-is
  if (typeof value === 'string') {
    return value;
  }

  // If number or boolean, convert to string
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  // ✅ Handle arrays (current_medications, allergies, etc.)
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          // Medication object: {name, dosage, frequency}
          const name = item.name || item.medication_name || '';
          const dosage = item.dosage || '';
          const frequency = item.frequency || '';
          return `${name} ${dosage} ${frequency}`.trim();
        }
        return String(item);
      })
      .filter(Boolean)
      .join(', ');
  }

  // ✅ Handle objects (ROS fields: general, respiratory, etc.)
  if (typeof value === 'object') {
    // Check if it has a symptoms array (ROS structure)
    if (Array.isArray(value.symptoms)) {
      const symptoms = value.symptoms.join(', ');
      const duration = value.duration ? ` (Duration: ${value.duration})` : '';
      const severity = value.severity ? ` [${value.severity}]` : '';
      return `${symptoms}${duration}${severity}`;
    }

    // Fallback: JSON stringify with formatting
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return String(value);
    }
  }

  // Fallback
  return String(value);
}

interface FormFieldProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  tabName: string;
  fieldName: string;
  defaultValue?: any;
  onManualChange?: (value: any) => void;
  showConfidence?: boolean;
  confidenceThreshold?: number;
  formatValue?: (value: any) => string; // ✅ NEW: Custom formatter for complex values
}

export default function FormField({
  tabName,
  fieldName,
  defaultValue = '',
  onManualChange,
  showConfidence,
  confidenceThreshold = 0.7,
  formatValue, // ✅ NEW: Custom formatter
  ...textFieldProps
}: FormFieldProps) {
  const {
    getFieldValue,
    getFieldConfidence,
    getFieldSource,
    hasLowConfidence,
    updateField,
  } = useFormData();

  const { showConfidence: globalShowConfidence } = useConfidenceToggle();

  // Use prop if explicitly provided, otherwise use global toggle
  const effectiveShowConfidence = showConfidence !== undefined ? showConfidence : globalShowConfidence;

  const rawValue = getFieldValue(tabName, fieldName, defaultValue);

  // ✅ NEW: Format value if formatter provided, otherwise use default formatting
  const value = formatValue ? formatValue(rawValue) : formatDefaultValue(rawValue);

  const confidence = getFieldConfidence(tabName, fieldName);
  const source = getFieldSource(tabName, fieldName);
  const lowConfidence = hasLowConfidence(tabName, fieldName, confidenceThreshold);

  // Glow effect: detect when value changes from an AI source
  const prevValueRef = useRef<string>(value);
  const [isGlowing, setIsGlowing] = useState(false);
  const glowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (
      value !== prevValueRef.current &&
      value !== '' &&
      source !== 'manual' &&
      source !== 'database'
    ) {
      setIsGlowing(true);
      if (glowTimerRef.current) clearTimeout(glowTimerRef.current);
      glowTimerRef.current = setTimeout(() => setIsGlowing(false), 8000);
    }
    prevValueRef.current = value;
  }, [value, source]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    updateField(tabName, fieldName, newValue);
    onManualChange?.(newValue);
  };

  const getConfidenceColor = () => {
    if (confidence >= 0.9) return '#009688'; // Teal - high confidence
    if (confidence >= 0.7) return '#ff9800'; // Orange - medium confidence
    return '#e91e63'; // Pink - low confidence (needs review)
  };

  const getSourceIcon = () => {
    switch (source) {
      case 'ambient':
        return <Check sx={{ fontSize: 14 }} />;
      case 'llm_correction':
        return <Warning sx={{ fontSize: 14 }} />;
      case 'manual':
        return <Edit sx={{ fontSize: 14 }} />;
      default:
        return undefined;
    }
  };

  const getSourceLabel = () => {
    switch (source) {
      case 'ambient':
        return 'Auto-filled from conversation';
      case 'llm_correction':
        return 'AI-corrected';
      case 'manual':
        return 'Manually entered';
      default:
        return 'Not filled';
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        {...textFieldProps}
        value={value}
        onChange={handleChange}
        sx={{
          ...textFieldProps.sx,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: isGlowing
                ? '#009688'
                : lowConfidence
                  ? '#e91e63'
                  : undefined,
              borderWidth: isGlowing || lowConfidence ? 2 : 1,
            },
            ...(isGlowing && {
              animation: `${glowPulse} 1.5s ease-in-out infinite`,
              borderRadius: 1,
            }),
          },
        }}
        InputProps={{
          ...textFieldProps.InputProps,
          endAdornment: effectiveShowConfidence && source !== 'manual' && source !== 'database' && (
            <Tooltip title={`${getSourceLabel()} (${(confidence * 100).toFixed(0)}% confidence)`}>
              <Chip
                icon={getSourceIcon()}
                label={`${(confidence * 100).toFixed(0)}%`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: getConfidenceColor(),
                  color: 'white',
                  '& .MuiChip-icon': {
                    color: 'white',
                  },
                }}
              />
            </Tooltip>
          ),
        }}
      />
    </Box>
  );
}
